// frontend/src/components/booking-management/TimeSlotGrid.js

import { useEffect, useMemo, useRef, useState } from 'react';
import axios from '../../axiosConfig';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Clock, ChevronRight, Shuffle, AlertTriangle } from 'lucide-react';
import { formatDateISO, formatTimeString } from '../../utils/dateTimeFormatter';
import {
  findApplicableSchedule,
  getPickupStartInfo,
  normalizeBookingOccurrenceDate,
  resolveExactDelayIdentity,
  sortTimeSlots,
} from '../../utils/bookingScheduleUtils';
import OnCallRequestDialog from '../dialogs/OnCallRequestDialog';

export const buildOnCallSelectorScopeFingerprint = ({
  booking,
  selectedDates = [],
  availableDates = [],
  bookAllAvailableDates = false,
  canBookRecurring = false,
  routeId,
  pickupStopId,
  dropoffStopId,
}) => {
  const bookingMode = bookAllAvailableDates
    ? 'all'
    : (canBookRecurring ? 'multiple' : 'single');
  const sourceDates = bookAllAvailableDates
    ? availableDates
    : (canBookRecurring ? selectedDates : [booking?.date]);
  const normalizedDates = Array.from(new Set(
    sourceDates.map(normalizeBookingOccurrenceDate).filter(Boolean),
  )).sort();

  return JSON.stringify({
    bookingMode,
    dates: normalizedDates,
    routeId: Number(routeId) || null,
    pickupStopId: Number(pickupStopId) || null,
    dropoffStopId: Number(dropoffStopId) || null,
  });
};

const TimeSlotGrid = ({
  slots,
  booking,
  onSelect,
  selectedTime,
  selectedDates,
  bookAllAvailableDates,
  canBookRecurring,
  appliedSchedules,
  availableDates,
  theme,
  RouteId,
  currentEntity,
  minimumAdvanceHours,
  minimumAdvanceBookingScope,
  waypoints,
  pickupStopId,
  dropoffStopId,
  referenceData,
  segmentData, // Segment times for baseline ETA (OpenRouteService)
  slotAvailability = [],
  slotAvailabilityLoading = false,
  slotAvailabilityDate = null,
  selectedOccurrenceDate = null,
  isExactSelectionSnapshotCurrent = false,
}) => {
  const { t } = useTranslation();
  const { cn, background, text } = theme;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOnCallModalOpen, setIsOnCallModalOpen] = useState(false);
  const [currentOnCallSlot, setCurrentOnCallSlot] = useState(null);
  const [existingBookings, setExistingBookings] = useState([]);
  const isProcessing = false;
  const [isLoading, setIsLoading] = useState(false);
  // Waitlist confirmation dialog state
  const [showWaitlistConfirm, setShowWaitlistConfirm] = useState(false);
  const [pendingWaitlistSlot, setPendingWaitlistSlot] = useState(null);
  const sortedSlots = sortTimeSlots(slots);
  const { isPickupStart, startName } = getPickupStartInfo(waypoints, pickupStopId);
  const normalizedRequestedRiders = Math.max(parseInt(booking?.riders, 10) || 1, 1);
  const selectedDelayIdentity = useMemo(() => resolveExactDelayIdentity({
    slotAvailability,
    slotAvailabilityDate,
    slotAvailabilityLoading,
    selectedOccurrenceDate,
    booking,
  }), [
    booking,
    selectedOccurrenceDate,
    slotAvailability,
    slotAvailabilityDate,
    slotAvailabilityLoading,
  ]);
  const selectorScopeFingerprint = useMemo(() => buildOnCallSelectorScopeFingerprint({
    booking,
    selectedDates,
    availableDates,
    bookAllAvailableDates,
    canBookRecurring,
    routeId: RouteId,
    pickupStopId,
    dropoffStopId,
  }), [
    RouteId,
    availableDates,
    bookAllAvailableDates,
    booking,
    canBookRecurring,
    dropoffStopId,
    pickupStopId,
    selectedDates,
  ]);
  const selectorScopeRef = useRef(selectorScopeFingerprint);
  const onCallRequestGenerationRef = useRef(0);
  const exactDateSnapshotPending = Boolean(
    selectedOccurrenceDate && !isExactSelectionSnapshotCurrent,
  );

  useEffect(() => {
    if (selectorScopeRef.current === selectorScopeFingerprint) {
      return;
    }

    // A response for an earlier date set must never reopen or populate the
    // selector after its modal scope has changed.
    selectorScopeRef.current = selectorScopeFingerprint;
    onCallRequestGenerationRef.current += 1;
    setIsOnCallModalOpen(false);
    setCurrentOnCallSlot(null);
    setExistingBookings([]);
    setIsLoading(false);
  }, [selectorScopeFingerprint]);

  const renderDelayBadge = (minutes) => (
    Number.isInteger(Number(minutes)) && Number(minutes) > 0 ? (
      <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
        {t('bookingSlotDelay.badge', { minutes: Number(minutes) })}
      </span>
    ) : null
  );

  const getAccessibleTooltip = (slot) => {
    if (slot?.accessibleVehicleFailureReason === 'no_vehicle_with_accessible_capacity') {
      if (slot?.remainingSeats != null) {
        return t('slotDisabledInsufficientAccessibleSeats', {
          remaining: Math.max(slot.remainingSeats, 0),
          requested: normalizedRequestedRiders,
        });
      }

      return t('vehicleAssignment.noVehicleWithAccessibleCapacity');
    }

    if (slot?.accessibleVehicleFailureReason === 'no_vehicle_with_luggage_capacity') {
      return t('slotDisabledInsufficientLuggage');
    }

    return slot?.accessibleVehicleOfflineMessage || t('slotDisabledNoAccessibleVehicle');
  };

  // Get tooltip message for slot state (disabled or potential with reorder)
  const getSlotTooltip = (slot) => {
    // Check for potential with reorder (not disabled but has special state)
    if (slot?.potentialWithReorder && slot?.disabledReason === 'potentialWithReorder') {
      return t('slotPotentialWithReorder');
    }

    if (
      slot?.type === 'on_call'
      && Array.isArray(slot.operationalBlockedIntervals)
      && slot.operationalBlockedIntervals.length > 0
      && !slot.disabled
    ) {
      return t('operationalUnavailability.partialWindowNotice');
    }

    if (!slot?.disabled) return '';
    switch (slot.disabledReason) {
      case 'operationalUnavailability':
        return t('operationalUnavailability.bookingConflict');
      case 'availabilityUnresolved':
        return t('operationalUnavailability.availabilityUnresolved');
      case 'tooSoon':
        return t('slotDisabledTooSoon', { hours: minimumAdvanceHours });
      case 'fullyBooked':
        return t('slotDisabledFullyBooked');
      case 'insufficientCapacity':
        return t('slotDisabledInsufficientSeats');
      case 'insufficientAccessibleCapacity':
        return getAccessibleTooltip(slot);
      case 'insufficientLuggage':
        return t('slotDisabledInsufficientLuggage');
      case 'insufficientBoth':
        return t('slotDisabledInsufficientBoth');
      case 'pickupStopPassed':
        return t('slotDisabledPickupStopPassed');
      case 'bookingPickupUnavailable':
        return t('bookingStopEligibility.pickupUnavailable');
      case 'bookingDropoffUnavailable':
        return t('bookingStopEligibility.dropoffUnavailable');
      case 'bookingStopsUnavailable':
        return t('bookingStopEligibility.bothUnavailable');
      case 'bookingStopEligibilityUnresolved':
        return t('bookingStopEligibility.unresolved');
      case 'noAvailableGap':
        return t('slotDisabledNoAvailableGap');
      case 'noAccessibleVehicle':
        return getAccessibleTooltip(slot);
      case 'noVehicleAvailable':
        return t('vehicleAssignment.noVehicleAvailable');
      case 'inPast':
      default:
        return t('slotDisabledPast');
    }
  };

  const renderSeatIndicator = (slot) => {
    // Don't show capacity for on-call slots (capacity varies per time within the window)
    if (slot.type === 'on_call') {
      return null;
    }

    // For potential with reorder slots, show special indicator
    if (slot.potentialWithReorder && slot.disabledReason === 'potentialWithReorder') {
      return (
        <span className="text-xs mt-1 flex items-center gap-1 text-amber-600 dark:text-amber-400">
          <Shuffle className="w-3 h-3" />
          {t('potentialSeats')}
        </span>
      );
    }

    if (
      slot.remainingSeats == null
      || slot.totalCapacity == null
      || Number(slot.totalCapacity) <= 0
    ) {
      return null;
    }

    // Show both passenger and luggage capacity for fixed slots only
    const hasLuggageCapacity = slot.totalLuggageCapacity > 0;

    return (
      <div className="text-xs mt-1 flex flex-col">
        <span>
          {t('slotCapacityLabel', {
            remaining: Math.max(slot.remainingSeats, 0),
            total: slot.totalCapacity,
          })}
        </span>
        {hasLuggageCapacity && slot.remainingLuggage != null && (
          <span className="text-gray-500 dark:text-gray-400">
            {t('slotLuggageCapacityLabel', {
              remaining: Math.max(slot.remainingLuggage, 0),
              total: slot.totalLuggageCapacity,
            })}
          </span>
        )}
      </div>
    );
  };

  // Handle waitlist confirmation
  const handleWaitlistConfirm = () => {
    if (pendingWaitlistSlot) {
      // User confirmed - proceed with the slot selection
      proceedWithSlotSelection(pendingWaitlistSlot);
    }
    setShowWaitlistConfirm(false);
    setPendingWaitlistSlot(null);
  };

  const handleWaitlistCancel = () => {
    setShowWaitlistConfirm(false);
    setPendingWaitlistSlot(null);
  };

  // Core slot selection logic (called after waitlist confirmation if needed)
  const proceedWithSlotSelection = (slot) => {
    if (slot.type === 'fixed') {
      onSelect(slot.time, {
        isWaitlist: slot.potentialWithReorder,
        slot
      });
      setIsExpanded(false);
    }
  };

  const handleSlotSelect = async (slot, event) => {
    event?.preventDefault();
    if (slot.disabled || exactDateSnapshotPending) {
      return;
    }

    // Check if this is a waitlist slot (over capacity but potential with reorder)
    const isWaitlistSlot = slot.potentialWithReorder && slot.disabledReason === 'potentialWithReorder';

    if (slot.type === 'fixed') {
      if (isWaitlistSlot) {
        // Show waitlist confirmation dialog
        setPendingWaitlistSlot(slot);
        setShowWaitlistConfirm(true);
        return;
      }
      onSelect(slot.time, { slot });
      setIsExpanded(false);
      return;
    }

    const requestScope = selectorScopeFingerprint;
    const requestGeneration = onCallRequestGenerationRef.current + 1;
    onCallRequestGenerationRef.current = requestGeneration;
    setIsLoading(true);
    try {
      let dateToCheck;
      if (canBookRecurring) {
        dateToCheck = bookAllAvailableDates ? availableDates : selectedDates;
      } else {
        dateToCheck = formatDateISO(booking?.date);
      }

      if (!dateToCheck) {
        toast.error(t('pleaseSelectADateFirst'));
        return;
      }

      const normalizedDate = Array.isArray(dateToCheck) ? dateToCheck[0] : dateToCheck;
      // The occurrence preview already applied schedule priority. Preserve
      // that identity instead of resolving an overlapping window by time
      // again in the component.
      const schedule = appliedSchedules.find(
        (candidate) => Number(candidate.id) === Number(slot.scheduleId)
      ) || findApplicableSchedule(
        normalizedDate,
        `${slot.startTime}-${slot.endTime}`,
        appliedSchedules,
      );

      if (!schedule) {
        toast.error(t('noValidScheduleFoundForSelectedTime'));
        return;
      }

      const response = await axios.get(`/api/bookings/${currentEntity.entityId}/existing-bookings`, {
        params: {
          RouteId,
          scheduleId: schedule.id,
          dates: dateToCheck,
          startTime: slot.startTime,
          endTime: slot.endTime,
          pickupWaypointId: pickupStopId,
          dropoffWaypointId: dropoffStopId,
          riders: normalizedRequestedRiders,
        },
      });

      if (
        onCallRequestGenerationRef.current !== requestGeneration
        || selectorScopeRef.current !== requestScope
      ) {
        return;
      }

      setExistingBookings(response.data);
      setCurrentOnCallSlot({
        ...slot,
        date: normalizedDate,
        schedule,
        dates: dateToCheck,
        selectorScopeFingerprint: requestScope,
      });
      setIsOnCallModalOpen(true);
    } catch (error) {
      if (
        onCallRequestGenerationRef.current !== requestGeneration
        || selectorScopeRef.current !== requestScope
      ) {
        return;
      }
      console.error('failedToFetchExistingBookings:', error);
      toast.error(t('failedToFetchExistingBookings'));
    } finally {
      if (
        onCallRequestGenerationRef.current === requestGeneration
        && selectorScopeRef.current === requestScope
      ) {
        setIsLoading(false);
      }
    }
  };

  const closeOnCallDialog = () => {
    onCallRequestGenerationRef.current += 1;
    setIsOnCallModalOpen(false);
    setCurrentOnCallSlot(null);
    setExistingBookings([]);
    setIsLoading(false);
  };

  return (
    <div className="mt-4">
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'flex items-center justify-between p-2 border rounded cursor-pointer',
          background.primary,
          'hover:bg-gray-50 dark:hover:bg-gray-700',
          'border-gray-200 dark:border-gray-700',
        )}
      >
        <div className="flex items-center">
          <Clock className="w-4 h-4 mr-2 text-blue-500" />
          <span className={cn('text-sm font-medium', text.primary)}>
            {isPickupStart
              ? t('availableTimes')
              : t('availableDepartureTimesWithStart', { startName })}
          </span>
        </div>
        {selectedTime && !isExpanded && (
          <div className="flex items-center gap-2">
            <span className={cn('text-sm', text.secondary)}>{formatTimeString(selectedTime)}</span>
            {selectedDelayIdentity.exact && renderDelayBadge(selectedDelayIdentity.minutes)}
          </div>
        )}
        <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
      </div>

      {isExpanded && (
        <div className="grid grid-cols-2 gap-2 mt-2 sm:grid-cols-4">
          {sortedSlots.map((slot, index) => {
            // Check if slot has potential with reorder
            const hasPotentialWithReorder = slot.potentialWithReorder && slot.disabledReason === 'potentialWithReorder';
            const hasPartialOperationalClosure = (
              slot.type === 'on_call'
              && !slot.disabled
              && Array.isArray(slot.operationalBlockedIntervals)
              && slot.operationalBlockedIntervals.length > 0
            );
            const isSlotDisabled = Boolean(slot.disabled || exactDateSnapshotPending);

            return (
            <button
              key={`${slot.type}-${slot.time || slot.startTime}-${index}`}
              onClick={(event) => handleSlotSelect(slot, event)}
              disabled={isSlotDisabled}
              className={cn(
                'p-2 text-sm rounded transition-colors flex flex-col items-center relative',
                isSlotDisabled
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'
                  : hasPotentialWithReorder
                  ? 'bg-amber-50 border-2 border-amber-400 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:border-amber-500 dark:text-amber-300 dark:hover:bg-amber-900/50'
                  : slot.type === 'fixed' && selectedTime === slot.time
                  ? 'bg-blue-500 text-white dark:bg-blue-600'
                  : slot.type === 'on_call' && selectedTime?.includes(slot.startTime)
                  ? 'bg-blue-600 text-white dark:bg-blue-500'
                  : cn(background.secondary, 'hover:bg-gray-200 dark:hover:bg-gray-600', text.primary),
              )}
              title={exactDateSnapshotPending
                ? t('loadingSlotAvailability')
                : getSlotTooltip(slot)}
            >
              {slot.type === 'fixed' ? (
                <>
                  <span>{slot.time}</span>
                  {isExactSelectionSnapshotCurrent
                    && renderDelayBadge(slot?.operationalDelay?.originDepartureMinutes)}
                </>
              ) : (
                <>
                  <span>{t('On-Call')}</span>
                  <span className="text-xs">
                    {slot.startTime} - {slot.endTime}
                  </span>
                  {hasPartialOperationalClosure && (
                    <span className="mt-1 flex items-center gap-1 text-[10px] leading-tight text-amber-700 dark:text-amber-300">
                      <AlertTriangle className="h-3 w-3 shrink-0" />
                      {t('operationalUnavailability.partialWindowNotice')}
                    </span>
                  )}
                </>
              )}
              {renderSeatIndicator(slot)}
              {slot.disabled && (
                <span className="mt-1 text-[10px] leading-tight">
                  {getSlotTooltip(slot)}
                </span>
              )}
            </button>
            );
          })}
        </div>
      )}

      {slotAvailabilityLoading && (
        <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
          <Clock className="w-3 h-3" />
          <span>{t('loadingSlotAvailability')}</span>
        </div>
      )}

      {(() => {
        // Determine if message should be shown based on scope
        const shouldShowAdvanceBookingMessage = slots.some(
          (slot) => slot.disabledReason === 'tooSoon',
        ) && minimumAdvanceHours > 0 && (
          minimumAdvanceBookingScope === 'all_stops' ||
          (minimumAdvanceBookingScope === 'start_only' && isPickupStart)
        );

        return shouldShowAdvanceBookingMessage && (
          <div
            className={cn(
              'mt-2 p-2 rounded-md text-xs',
              'bg-blue-50 dark:bg-blue-900/20',
              'border border-blue-200 dark:border-blue-800',
              'text-blue-700 dark:text-blue-300',
            )}
          >
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                {minimumAdvanceBookingScope === 'start_only'
                  ? t('minimumAdvanceBookingNoticeStartOnly', { hours: minimumAdvanceHours })
                  : t('minimumAdvanceBookingNotice', { hours: minimumAdvanceHours })
                }
              </span>
            </div>
          </div>
        );
      })()}

      <OnCallRequestDialog
        isOpen={isOnCallModalOpen}
        onClose={closeOnCallDialog}
        onConfirm={(time) => {
          const modalScopeIsCurrent = currentOnCallSlot?.selectorScopeFingerprint
            && currentOnCallSlot.selectorScopeFingerprint === selectorScopeRef.current;
          const exactDateIsCurrent = !selectedOccurrenceDate || (
            isExactSelectionSnapshotCurrent
            && normalizeBookingOccurrenceDate(currentOnCallSlot?.date) === selectedOccurrenceDate
            && normalizeBookingOccurrenceDate(slotAvailabilityDate) === selectedOccurrenceDate
          );
          if (!modalScopeIsCurrent || !exactDateIsCurrent) {
            closeOnCallDialog();
            return;
          }

          const exactTrip = slotAvailability.find(
            (entry) => String(entry?.time || '').slice(0, 5) === time
          );
          onSelect(time, {
            slot: exactTrip || {
              ...currentOnCallSlot,
              time,
              tripId: null
            }
          });
          closeOnCallDialog();
          setIsExpanded(false);
        }}
        slot={currentOnCallSlot}
        existingBookings={existingBookings}
        slotAvailability={slotAvailability}
        slotAvailabilityDate={slotAvailabilityDate}
        isExactSelectionSnapshotCurrent={isExactSelectionSnapshotCurrent}
        bookingType={bookAllAvailableDates ? 'all' : canBookRecurring ? 'multiple' : 'single'}
        selectedDates={selectedDates}
        startDate={bookAllAvailableDates ? availableDates[0] : null}
        endDate={bookAllAvailableDates ? currentOnCallSlot?.schedule?.end_date : null}
        isProcessing={isProcessing || isLoading}
        waypoints={waypoints}
        pickupStopId={pickupStopId}
        dropoffStopId={dropoffStopId}
        referenceData={referenceData}
        segmentData={segmentData}
        entityId={currentEntity?.entityId}
        ridersRequested={booking?.riders}
        luggageRequested={booking?.luggage}
      />

      {/* Waitlist Confirmation Dialog */}
      {showWaitlistConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={cn(
            'max-w-md w-full mx-4 p-6 rounded-lg shadow-xl',
            'bg-white dark:bg-gray-800'
          )}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
                <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className={cn('text-lg font-semibold mb-2', text.primary)}>
                  {t('waitlistBookingTitle')}
                </h3>
                <p className={cn('text-sm mb-4', text.secondary)}>
                  {t('waitlistBookingWarning')}
                </p>
                <ul className={cn('text-sm mb-4 list-disc list-inside space-y-1', text.secondary)}>
                  <li>{t('waitlistBookingPoint1')}</li>
                  <li>{t('waitlistBookingPoint2')}</li>
                  <li>{t('waitlistBookingPoint3')}</li>
                </ul>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={handleWaitlistCancel}
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-md',
                  'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600',
                  text.primary
                )}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleWaitlistConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md"
              >
                {t('waitlistBookingConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeSlotGrid;
